import { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import BetDialog from "@/components/BetDialog";
import DateRange from "@/components/DateRange";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  UploadCloud,
  Plus,
  Loader2,
  Pencil,
  Trash2,
  ImageIcon,
  Calendar,
} from "lucide-react";

const resultStyles = {
  win: "bg-[#34C759]/15 text-[#34C759] border-[#34C759]/30",
  loss: "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/30",
  void: "bg-muted text-muted-foreground border-border",
  pending: "bg-[#FF9500]/15 text-[#FF9500] border-[#FF9500]/30",
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Bets() {
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [drag, setDrag] = useState(false);
  const [dialog, setDialog] = useState({ open: false, initial: null, betId: null });
  const [deleteId, setDeleteId] = useState(null);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [range, setRange] = useState({ start: "", end: "", preset: "all" });
  const [resultFilter, setResultFilter] = useState("all");
  const [marketFilter, setMarketFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [seasonFilter, setSeasonFilter] = useState("all");
  const [markets, setMarkets] = useState([]);
  const [cats, setCats] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const fileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (range.start) params.start = range.start;
    if (range.end) params.end = range.end;
    if (resultFilter !== "all") params.result = resultFilter;
    if (marketFilter !== "all") params.market = marketFilter;
    if (categoryFilter !== "all") params.category = categoryFilter;
    if (seasonFilter !== "all") params.season = seasonFilter;
    try {
      const { data } = await api.get("/bets", { params });
      setBets(data);
    } finally {
      setLoading(false);
    }
  }, [range, resultFilter, marketFilter, categoryFilter, seasonFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/bets/meta/markets").then((r) => setMarkets(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/bets/meta/seasons").then((r) => setSeasons(r.data)).catch(() => {});
  }, [dialog.open]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/image\/(png|jpe?g|webp)/.test(file.type)) {
      toast.error("Please upload a PNG, JPG or WEBP image.");
      return;
    }
    setExtracting(true);
    try {
      const b64 = await fileToBase64(file);
      const { data } = await api.post("/bets/extract", { image_base64: b64 });
      toast.success("Bet details extracted, review & save.");
      setDialog({ open: true, initial: { ...data, image_base64: b64 }, betId: null });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Extraction failed");
    } finally {
      setExtracting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/bets/${deleteId}`);
      toast.success("Bet deleted");
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  const confirmClear = async () => {
    setClearing(true);
    try {
      const { data } = await api.delete("/bets");
      toast.success(`Cleared ${data.deleted} bet${data.deleted === 1 ? "" : "s"}`);
      setClearOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Clear failed");
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
            Bets
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Upload a slip screenshot or log a bet manually.
          </p>
        </div>
        <div className="flex items-center gap-2 self-start">
          {bets.length > 0 && (
            <Button
              variant="outline"
              onClick={() => setClearOpen(true)}
              data-testid="clear-history-button"
              className="rounded-full font-semibold text-destructive border-destructive/30 hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" /> Clear history
            </Button>
          )}
          <Button
            onClick={() => setDialog({ open: true, initial: null, betId: null })}
            data-testid="add-bet-button"
            className="rounded-full font-semibold"
          >
            <Plus className="h-4 w-4" /> Add Manually
          </Button>
        </div>
      </div>

      {/* Upload zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => !extracting && fileRef.current?.click()}
        data-testid="upload-zone"
        className={`glass rounded-3xl border-2 border-dashed p-10 sm:p-12 text-center cursor-pointer transition-colors duration-200 ${
          drag ? "border-primary bg-primary/5" : "border-border"
        }`}
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          data-testid="file-input"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        {extracting ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="font-medium">Reading your bet slip…</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-1">
              <UploadCloud className="h-7 w-7 text-primary" />
            </div>
            <p className="font-display font-bold text-lg">Drop your bet screenshot</p>
            <p className="text-muted-foreground text-sm">
              PNG, JPG or WEBP · AI extracts the details automatically
            </p>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <DateRange range={range} setRange={setRange} />
        <Select value={resultFilter} onValueChange={setResultFilter}>
          <SelectTrigger className="rounded-full h-9 w-32" data-testid="filter-result">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All results</SelectItem>
            <SelectItem value="win">Wins</SelectItem>
            <SelectItem value="loss">Losses</SelectItem>
            <SelectItem value="void">Void</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        {markets.length > 0 && (
          <Select value={marketFilter} onValueChange={setMarketFilter}>
            <SelectTrigger className="rounded-full h-9 w-44" data-testid="filter-market">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All markets</SelectItem>
              {markets.map((m) => (
                <SelectItem key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {cats.length > 0 && (
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="rounded-full h-9 w-44" data-testid="filter-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All competitions</SelectItem>
              {cats.map((c) => (
                <SelectItem key={c.id} value={c.name}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {seasons.length > 0 && (
          <Select value={seasonFilter} onValueChange={setSeasonFilter}>
            <SelectTrigger className="rounded-full h-9 w-36" data-testid="filter-season">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All seasons</SelectItem>
              {seasons.map((sn) => (
                <SelectItem key={sn} value={sn}>
                  {sn}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="h-40 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : bets.length === 0 ? (
        <div className="glass rounded-3xl p-10 text-center" data-testid="bets-empty">
          <ImageIcon className="h-9 w-9 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No bets match your filters yet.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="bets-list">
          <AnimatePresence>
            {bets.map((b, i) => (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3, delay: Math.min(i * 0.03, 0.3) }}
                className="glass rounded-2xl p-4 sm:p-5 flex items-center gap-4"
                data-testid="bet-row"
              >
                {b.image_base64 ? (
                  <img
                    src={b.image_base64}
                    alt="slip"
                    className="h-14 w-14 rounded-xl object-cover shrink-0 hidden sm:block"
                  />
                ) : (
                  <div className="h-14 w-14 rounded-xl bg-secondary hidden sm:flex items-center justify-center shrink-0">
                    <ImageIcon className="h-5 w-5 text-muted-foreground" />
                  </div>
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold truncate">
                      {b.match || b.selection || b.market || "Bet"}
                    </span>
                    <Badge
                      variant="outline"
                      className={`capitalize text-[11px] rounded-full ${resultStyles[b.result]}`}
                    >
                      {b.result}
                    </Badge>
                    {b.category && (
                      <Badge
                        variant="outline"
                        className="text-[11px] rounded-full bg-primary/10 text-primary border-primary/20"
                        data-testid="bet-category-badge"
                      >
                        {b.category}
                      </Badge>
                    )}
                    {b.season && (
                      <Badge
                        variant="outline"
                        className="text-[11px] rounded-full bg-secondary/70 text-muted-foreground"
                        data-testid="bet-season-badge"
                      >
                        {b.season}
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground truncate">
                    {[b.market, b.selection].filter(Boolean).join(" · ") || "-"}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> {b.bet_date}
                    </span>
                    <span>@ {b.odds}</span>
                    <span>Stake {money(b.stake)}</span>
                  </div>
                </div>

                <div className="text-right shrink-0">
                  <div
                    className={`font-display font-bold text-lg ${
                      b.profit > 0
                        ? "text-[#34C759]"
                        : b.profit < 0
                        ? "text-[#FF3B30]"
                        : "text-muted-foreground"
                    }`}
                    data-testid="bet-profit"
                  >
                    {b.profit > 0 ? "+" : ""}
                    {money(b.profit)}
                  </div>
                  <div className="text-xs text-muted-foreground">{b.roi}% ROI</div>
                </div>

                <div className="flex flex-col gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-full"
                    data-testid="edit-bet"
                    onClick={() =>
                      setDialog({ open: true, initial: b, betId: b.id })
                    }
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-full text-muted-foreground hover:text-destructive"
                    data-testid="delete-bet"
                    onClick={() => setDeleteId(b.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      <BetDialog
        open={dialog.open}
        initial={dialog.initial}
        betId={dialog.betId}
        onClose={() => setDialog({ open: false, initial: null, betId: null })}
        onSaved={load}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-3xl" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this bet?</AlertDialogTitle>
            <AlertDialogDescription>
              This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="confirm-delete"
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={clearOpen} onOpenChange={(o) => !o && setClearOpen(false)}>
        <AlertDialogContent className="rounded-3xl" data-testid="clear-history-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear your entire bet history?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes <b>all</b> of your logged bets and can't be undone.
              Your competitions and settings stay.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmClear}
              disabled={clearing}
              data-testid="confirm-clear-history"
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {clearing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Clear everything"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
